import React, { useState } from "react";
import { DualHeader } from "@/components/DualHeader";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { toast } from "@/hooks/use-toast";
import { HeroSection } from "@/components/hunting/HeroSection";
import { HuntTypesSection } from "@/components/hunting/HuntTypesSection";
import { BookingForm } from "@/components/hunting/BookingForm";
import { AssistanceFooter } from "@/components/cart/AssistanceFooter";

const huntTypes = [
  {
    id: "antelope",
    title: "Antelope Hunt",
    season: "Jul 14, 2024 to Oct 30, 2024",
    description: "Guided antelope hunts in a 450 square mile hunting area with high success rates. Available for archery, black powder, and rifle hunters.",
    huntingArea: "450 square miles",
    successRate: 95
  }, 
  {
    id: "elk",
    title: "Elk Hunt",
    season: "Sep 1, 2025 to Oct 31, 2025",
    description: "Guided elk hunts in prime elk habitat with experienced tribal guides. Available for rifle, bow, or muzzleloader.",
    huntingArea: "Duck Valley Reservation",
    successRate: 85
  }, 
  {
    id: "pictures",
    title: "Pictures",
    description: "View our gallery of wildlife and hunting photos from the Duck Valley Indian Reservation.",
    pictures: [
      {
        src: "public/assets/icons/IMG_0561.jpg",
        description: "Peaceful fishing at dawn on tribal waters"
      },
      {
        src: "https://images.unsplash.com/photo-1466721591366-2d5fba72006d",
        description: "Wildlife thriving on tribal lands"
      },
      {
        src: "https://images.unsplash.com/photo-1493962853295-0fd70327578a",
        description: "Pristine hunting grounds"
      },
      {
        src: "https://images.unsplash.com/photo-1439886183900-e79ec0057170",
        description: "Dawn breaks over the hunting grounds"
      }
    ]
  }
];

const packageTypes = [
  {
    id: "non-hunter",
    title: "Non-Hunter",
    price: 150,
    description: "Per day rate for non-hunters accompanying the hunting party",
    days: 1,
    badgeText: "Non-Hunter",
    badgeColor: "bg-blue-500"
  }, 
  {
    id: "transportation",
    title: "Transportation",
    price: 400,
    description: "Transportation from Boise/Elko",
    days: 1,
    badgeText: "Transportation",
    badgeColor: "bg-purple-500"
  }, 
  {
    id: "1hunter-1guide",
    title: "1 Hunter/1 Guide",
    price: 2000,
    description: "3-day guided hunt, one antelope",
    days: 3,
    hunters: 1,
    guides: 1
  }, 
  {
    id: "youth",
    title: "Youth Hunter (12-17)",
    price: 2000,
    description: "Must be accompanied by an adult hunter",
    days: 3,
    hunters: 1,
    badgeText: "Youth",
    badgeColor: "bg-green-500"
  }, 
  {
    id: "2hunters-1guide",
    title: "2 Hunters/1 Guide",
    price: 4000,
    description: "3-day guided hunt, one antelope per hunter",
    days: 3,
    hunters: 2,
    guides: 1
  }
];

const HuntingPermits = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  
  const [activeHuntTab, setActiveHuntTab] = useState("elk");
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedHunt, setSelectedHunt] = useState<string | null>("elk");
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [huntingLicense, setHuntingLicense] = useState("");
  
  const handleBackToHome = () => {
    navigate("/");
  };
  
  const handleSelectHunt = (huntId: string) => {
    setActiveHuntTab(huntId);
    setSelectedHunt(huntId);
    setSelectedPackage(null);
    setShowBookingForm(false);
  };
  
  const handleReserveClick = () => {
    // Scroll to contact information at bottom of page
    const contactSection = document.getElementById("contact-info");
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customerName || !customerEmail || !phoneNumber) {
      toast({
        title: "Missing information",
        description: "Please fill out all required fields",
        variant: "destructive"
      });
      return;
    }
    
    const hunt = huntTypes.find(h => h.id === selectedHunt);
    const pkg = packageTypes.find(p => p.id === selectedPackage);
    
    if (!hunt || !pkg) {
      toast({
        title: "Selection error",
        description: "Please select a valid hunt type and package",
        variant: "destructive"
      });
      return;
    }

    const huntingId = `hunting-${hunt.id}-${pkg.id}-${Date.now()}`;
    
    const bookingData = {
      id: "",
      type: "hunting" as const,
      permitCategory: "hunting",
      permitName: `${hunt.title} - ${pkg.title}`,
      isAnnual: false,
      customerName,
      customerEmail,
      startDate: new Date(hunt.season.split(" to ")[0]),
      endDate: new Date(hunt.season.split(" to ")[1]),
      includesCamping: true,
      basePrice: pkg.price,
      totalPrice: pkg.price,
      phoneNumber,
      address,
      city,
      state,
      zipCode,
      additionalDetails: `Hunting License #: ${huntingLicense}`,
      siteId: huntingId
    };
    
    addItem(bookingData);
    navigate("/cart");
  };
  
  return (
    <div className="min-h-screen bg-tribal-light text-tribal-secondary">
      <DualHeader />
      
      <HeroSection handleBackToHome={handleBackToHome} />
      
      <HuntTypesSection 
        huntTypes={huntTypes}
        activeHuntTab={activeHuntTab}
        setActiveHuntTab={setActiveHuntTab}
        selectedPackage={selectedPackage}
        setSelectedPackage={setSelectedPackage}
        handleSelectHunt={handleSelectHunt}
        handleReserveClick={handleReserveClick}
      />
      
      <section className="py-10 px-4 md:px-8 max-w-7xl mx-auto bg-slate-50">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-tribal-secondary text-white"></div>
          </div>
          <div className="space-y-6"></div>
        </div>
      </section>
      
      {showBookingForm && (
        <BookingForm 
          selectedHunt={selectedHunt}
          selectedPackage={selectedPackage}
          huntTypes={huntTypes}
          packageTypes={packageTypes}
          customerName={customerName}
          setCustomerName={setCustomerName}
          customerEmail={customerEmail}
          setCustomerEmail={setCustomerEmail}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          address={address}
          setAddress={setAddress}
          city={city}
          setCity={setCity}
          state={state}
          setState={setState}
          zipCode={zipCode}
          setZipCode={setZipCode}
          huntingLicense={huntingLicense}
          setHuntingLicense={setHuntingLicense}
          handleSubmit={handleSubmit}
          setShowBookingForm={setShowBookingForm}
        />
      )}
      
      {/* Contact Information Section */}
      <section id="contact-info" className="py-10 px-4 md:px-8 max-w-7xl mx-auto">
        <AssistanceFooter />
      </section>
    </div>
  );
};

export default HuntingPermits;
